import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';

interface IInvoiceCountyBreakdownReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

// Matches ClientsBilledReportDialog.tsx's own label/field convention (the gold standard for these
// report-parameter dialogs): explicit left-aligned nowrap labels with a fixed width/marginRight, and
// fields narrowed to 65% instead of stretched to the full row width.
const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', textAlign: 'left', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '65%' };

// Backs the Accounts Receivable "Invoice Breakdown By County" report - ported from
// XL_Invoice_County_Breakdown.sql, deployed on the report server as InvoiceCountyBreakdown (see
// ReportService.GetInvoiceCountyBreakdownReportFile for the confirmed BegDate|EndDate|State
// @Parameters shape - read directly off dbo.usp_InvoiceCountyBreakdown_Detail). State is optional -
// the underlying proc treats an empty value as "all states" - defaulted to ALL here instead of a
// separate "(Blank=All)" label, matching Oracle's own State field on this screen. Filename mirrors
// Oracle's own FILENAME field - it's purely a client-side download name (the report server itself
// doesn't take a filename parameter), defaulting to InvoiceCountyBreakdown and always getting a
// .xlsx extension.
const InvoiceCountyBreakdownReportDialog = (props: IInvoiceCountyBreakdownReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get: getReport, response: reportResponse } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const generateReport = async () => {
        const values = getValues();
        if (!values.begDate || !values.endDate) {
            setMessage('A beginning and ending date must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const params = new URLSearchParams({ begDate: values.begDate, endDate: values.endDate });
        if (values.state && values.state.toUpperCase() !== 'ALL') {
            params.set('state', values.state);
        }
        await getReport(`/v1.0/reports/GetReport/InvoiceCountyBreakdown?${params.toString()}`);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No invoices matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const baseName = (values.filename || 'InvoiceCountyBreakdown').trim().replace(/\.xlsx$/i, '');
        a.download = `${baseName || 'InvoiceCountyBreakdown'}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title="Invoice Breakdown By County" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="begDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="State"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="state"
                        type="text"
                        defaultVal="ALL"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="filename"
                        type="text"
                        defaultVal="InvoiceCountyBreakdown"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default InvoiceCountyBreakdownReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/InvoiceCountyBreakdownReportDialog.tsx